#!/usr/bin/env node

import figlet from 'figlet';
import { TikTokDownloader } from './lib/tiktok.js';
import chalk from 'chalk';
import inquirer from 'inquirer';
import ora from 'ora';
import boxen from 'boxen';
import gradient from 'gradient-string';
import fetch from 'node-fetch';
import { createRequire } from 'module';
import fs from 'fs';

const require = createRequire(import.meta.url);
const packageJson = require('./package.json');

// Enhanced console styling configuration
const mainBoxOptions = {
    padding: 1,
    margin: 1,
    borderStyle: 'double',
    borderColor: 'cyan',
    backgroundColor: '#1B1B1B'
};

const alertBoxOptions = {
    padding: 1,
    margin: 1,
    borderStyle: 'round',
    borderColor: 'yellow',
    backgroundColor: '#2D2D2D'
};

const successBoxOptions = {
    padding: 1,
    margin: 1,
    borderStyle: 'bold',
    borderColor: 'green',
    backgroundColor: '#1B1B1B'
};

const errorBoxOptions = {
    padding: 1,
    margin: 1,
    borderStyle: 'bold',
    borderColor: 'red',
    backgroundColor: '#1B1B1B'
};

// Enhanced gradient colors
const titleGradient = gradient([
    '#FF0000',
    '#FF7F00',
    '#FFFF00',
    '#00FF00',
    '#0000FF',
    '#4B0082',
    '#9400D3'
]);

const rainbowText = text => gradient(['#ff0000', '#00ff00', '#0000ff'])(text);
const neonText = text => gradient(['#00ff00', '#00ffff', '#ff00ff'])(text);
const goldText = text => gradient(['#FFD700', '#FFA500'])(text);

async function checkForUpdates() {
    const spinner = ora({
        text: rainbowText('Checking for updates...'),
        color: 'cyan',
        spinner: 'dots12'
    }).start();
    
    try {
        const response = await fetch('https://raw.githubusercontent.com/lyssadev/tk4-downloader/main/package.json');
        const data = await response.json();
        
        if (data.version !== packageJson.version) {
            spinner.info(boxen(
                `${chalk.yellow('⚡ UPDATE AVAILABLE ⚡')}\n\n` +
                `Current: ${chalk.dim(packageJson.version)} → Latest: ${chalk.green(data.version)}\n\n` +
                `Run: ${chalk.cyan('npm install -g tk4-downloader')} to update\n` +
                `${chalk.dim('Stay updated for the best experience!')}`,
                alertBoxOptions
            ));
        } else {
            spinner.succeed(chalk.green('✨ You are using the latest version!'));
        }
    } catch (error) {
        spinner.warn(chalk.yellow('⚠️ Could not check for updates'));
    }
}

function displayBanner() {
    console.clear();
    
    // Create a stunning ASCII art title
    const title = figlet.textSync('TK4', {
        font: 'ANSI Shadow',
        horizontalLayout: 'full',
        verticalLayout: 'default'
    });
    
    const subtitle = figlet.textSync('DOWNLOADER', {
        font: 'Small',
        horizontalLayout: 'fitted',
        verticalLayout: 'default'
    });

    const version = figlet.textSync(`v${packageJson.version}`, {
        font: 'Mini',
        horizontalLayout: 'fitted'
    });

    // Display the enhanced title with rainbow gradient
    console.log('\n');
    title.split('\n').forEach(line => console.log(titleGradient(line)));
    subtitle.split('\n').forEach(line => console.log(neonText(line)));
    version.split('\n').forEach(line => console.log(goldText(line)));
    
    // Display main info box
    console.log(boxen(
        `${chalk.bold(rainbowText('TK4 Downloader'))} ${chalk.dim(`v${packageJson.version}`)}\n\n` +
        `${chalk.blue('◆')} ${neonText('Download TikTok videos in FHD quality')}\n` +
        `${chalk.blue('◆')} ${chalk.red('Remove watermarks automatically')}\n` +
        `${chalk.blue('◆')} ${chalk.magenta('Modern CLI with real-time progress')}\n` +
        `${chalk.blue('◆')} ${chalk.yellow('Optimized for speed and quality')}\n` +
        `${chalk.blue('◆')} ${chalk.green('Multiple download sources')}\n` +
        `${chalk.blue('◆')} ${chalk.cyan('Smart caching system')}\n\n` +
        `${chalk.dim('━'.repeat(50))}\n` +
        `${chalk.dim('Created with')} ${chalk.red('♥')} ${chalk.dim('by')} ${chalk.cyan('lyssadev')} ${chalk.dim('&')} ${chalk.cyan('chifft')}\n` +
        `${chalk.dim('Optimized for Termux & Linux Users')}`,
        mainBoxOptions
    ));
}

async function validateTikTokUrl(url) {
    const regex = /^https:\/\/(www\.)?(vm\.)?(vt\.)?tiktok\.com\//;
    const response = await fetch(url);
    if (!response.ok || !regex.test(url)) {
        throw new Error('Invalid TikTok URL');
    }
    return true;
}

async function downloadTikTok(url) {
    const spinner = ora({
        text: neonText('Initializing download process...'),
        color: 'cyan',
        spinner: 'dots12'
    }).start();

    try {
        const downloader = new TikTokDownloader();
        const result = await downloader.downloadVideo(url);
        
        if (!result || !result.url) {
            throw new Error('Could not fetch video information');
        }

        const videoId = url.split('/').pop().split('?')[0] || Date.now();
        const filename = `tiktok_${videoId}.mp4`;

        // Download the video
        spinner.text = neonText('⬇️ Downloading video...');
        const response = await fetch(result.url);
        const buffer = await response.arrayBuffer();
        fs.writeFileSync(filename, Buffer.from(buffer));
        
        // Get detailed statistics
        const stats = downloader.getDetailedStats();
        
        spinner.succeed(chalk.green('✨ Download completed successfully!'));
        
        // Display enhanced success message
        console.log(boxen(
            `${chalk.green('✓')} ${chalk.bold('Download Summary')}\n\n` +
            `${chalk.blue('📁')} Filename: ${chalk.cyan(filename)}\n` +
            `${chalk.blue('👤')} Author: ${chalk.cyan(result.author)}\n` +
            `${chalk.blue('🎥')} Quality: ${chalk.green(result.quality)}\n` +
            `${chalk.blue('🔍')} Source: ${chalk.yellow(result.source)}\n` +
            `${chalk.blue('📍')} Location: ${chalk.cyan(process.cwd())}\n` +
            `${chalk.blue('⚡')} Status: ${chalk.green('Ready to play!')}\n` +
            `${chalk.blue('📊')} Success Rate: ${chalk.green(`${Math.round(stats.successRate)  }%`)}\n` +
            `${chalk.blue('⏱️')} Response Time: ${chalk.yellow(`${stats.averageSpeedMs  }ms`)}\n` +
            `${chalk.blue('💾')} Cache Status: ${stats.cacheSaves > 0 ? chalk.green('Hit') : chalk.yellow('Miss')}\n\n` +
            `${chalk.dim('Tip: Your video is ready to be shared!')}`,
            successBoxOptions
        ));

        // Display API performance if debug is enabled
        if (result.source && stats.apiUsage[result.source]) {
            const apiStats = stats.apiUsage[result.source];
            console.log(boxen(
                `${chalk.yellow('API Performance Metrics')}\n\n` +
                `${chalk.blue('🎯')} Success Rate: ${chalk.green(`${Math.round((apiStats.successes / apiStats.calls) * 100)  }%`)}\n` +
                `${chalk.blue('⚡')} Avg Response: ${chalk.cyan(`${Math.round(apiStats.averageResponseTime)  }ms`)}\n` +
                `${chalk.blue('📊')} Total Calls: ${chalk.yellow(apiStats.calls)}`,
                alertBoxOptions
            ));
        }

        return filename;
    } catch (error) {
        spinner.fail(chalk.red(`❌ Download failed: ${error.message}`));
        
        // Enhanced error display
        console.error(boxen(
            `${chalk.red('❌ Error Details')}\n\n` +
            `${chalk.red(error.message)}\n\n` +
            `${chalk.dim('Troubleshooting Tips:')}\n` +
            `${chalk.dim('1. Check your internet connection')}\n` +
            `${chalk.dim('2. Verify the TikTok URL is valid')}\n` +
            `${chalk.dim('3. Try again in a few moments')}\n` +
            `${chalk.dim('4. The video might be private or deleted')}`,
            errorBoxOptions
        ));
        
        throw error;
    }
}

// Enhanced graceful exit
process.on('SIGINT', () => {
    console.log(boxen(
        `${chalk.yellow('👋 Thanks for using TK4 Downloader!')}\n\n` +
        `${chalk.dim('Follow us on GitHub for updates:')}\n` +
        `${chalk.cyan('https://github.com/lyssadev/tk4-downloader')}\n\n` +
        `${chalk.dim('Star ⭐ the repository if you found it helpful!')}`,
        { padding: 1, margin: 1, borderColor: 'yellow', dimBorder: true }
    ));
    process.exit(0);
});

async function processUrls(urls) {
    const downloader = new TikTokDownloader();
    const results = await Promise.all(
        urls.map(async (url) => {
            try {
                return await downloader.downloadVideo(url);
            } catch (error) {
                console.error(chalk.red(`❌ Failed to download ${url}: ${error.message}`));
                return null;
            }
        })
    );
    return results.filter(result => result !== null);
}

async function main() {
    displayBanner();
    await checkForUpdates();

    let continueDownloading = true;
    while (continueDownloading) {
        try {
            const { url } = await inquirer.prompt([
                {
                    type: 'input',
                    name: 'url',
                    message: neonText('Enter TikTok video URL:'),
                    validate: validateTikTokUrl,
                    prefix: '🎥'
                }
            ]);

            await downloadTikTok(url);

            const answer = await inquirer.prompt([
                {
                    type: 'confirm',
                    name: 'continue',
                    message: 'Would you like to download another video?',
                    default: false
                }
            ]);
            continueDownloading = answer.continue;
        } catch (error) {
            const { retry } = await inquirer.prompt([
                {
                    type: 'confirm',
                    name: 'retry',
                    message: neonText('Would you like to try again?'),
                    default: true
                }
            ]);

            if (!retry) {
                process.exit(1);
            }
        }
    }
}

main(); 